import { Button, ButtonVariant, Tooltip } from "@merge-api/merge-javascript-shared";
import { GitCompare } from "lucide-react";
import { useEffect, useState } from "react";
import { GlobalHotKeys } from "react-hotkeys";
import { PaginatedAPIResponse, fetchWithAuth } from "../../../api-client/api_client";
import {
  ASYNC_TASK_EXECUTION_QUEUED_STATUSES,
  ASYNC_TASK_EXECUTION_RUNNING_STATUSES,
  AsyncTaskExecution,
  AsyncTaskExecutionPreview,
  BlueprintOperationType,
  BlueprintTestRunRequestErrorResponse,
  BlueprintTestPayload,
  BlueprintTriggerType,
  DISABLE_FILTER_BY_DATE_OPERATION_TYPES,
  JSONObjectSchema,
  ValidationProblem,
} from "../../../models/Blueprints";
import { CommonModelInfo, LinkedAccount } from "../../../models/Entities";
import { isAbstractConditionsForSelectiveSyncValid, isValidJSON } from "../../../utils";
import { initializeInputParams } from "../../blueprint-editor/utils/BlueprintEditorUtils";
import useFetchAsyncResult from "../../shared/hooks/useFetchAsyncResult";
import { showErrorToast, showWarningToast } from "../../shared/Toasts";
import useBlueprintContext from "../context/useBlueprintContext";
import { testBlueprint } from "../utils/BlueprintEditorAPIClient";
import {
  CODE_BLOCK_BRANCH_PRODUCTION_RUN_DISABLED_TOOLTIP,
  isRunBlueprintDisabledForCodeBlockBranch,
} from "../utils/BlueprintRunControls";
import AsyncTestRunsModal from "./AsyncTestRunsModal";
import AsyncTestRunUnsavedChangesModal from "./AsyncTestRunUnsavedChangesModal";

import { AbstractCondition } from "../../integration-builder/selective-sync-filters/types";
import { prepareAbstractConditionsForTestRun } from "../../integration-builder/selective-sync-filters/utils";
import { ScraperVersion } from "../../scraper/types";
import { AbstractConditionAction } from "../reducers/AbstractConditionsForSelectiveSyncReducer";
import BlueprintLeftPanelConsolePayload from "./left-panel-console/payload/BlueprintLeftPanelConsolePayload";
import BlueprintLeftPanelConsoleResultLogs from "./left-panel-console/BlueprintLeftPanelConsoleResultLogs";
import BlueprintLeftPanelConsoleResultSummary from "./left-panel-console/BlueprintLeftPanelConsoleResultSummary";
import BlueprintLeftPanelConsoleValidationError from "./left-panel-console/BlueprintLeftPanelConsoleValidationError";
import useBlueprintConfigurationError from "./left-panel-console/useBlueprintConfigurationError";
import BlueprintLeftPanelConsoleSelect from "./left-panel-console/BlueprintLeftPanelConsoleSelect";
import BlueprintLeftPanelConsoleSettings from "./left-panel-console/BlueprintLeftPanelConsoleSettings";
import BlueprintCommonModelDiffsDrawer, {
  canShowBlueprintCommonModelDiffsAction,
} from "./left-panel-console/BlueprintCommonModelDiffsDrawer";
import ReportFileUploadModal from "./ReportFileUploadModal";
import LeftPanelSectionHeader from "./shared/LeftPanelSectionHeader";
import useInitiateWebConnectorLiveRun from "./web-connector/hooks/useInitiateWebConnectorLiveRun";
import LiveRunChips from "./web-connector/LiveRunChips";
import WebConnectorLiveRunResults from "./web-connector/WebConnectorLiveRunResults";
import {
  getBlueprintTestRunErrorMessage,
  handleTimeInputChange,
} from "./shared/left-panel-helpers";
import { CodeBlockBranchValue } from "../../../constants";

interface Props {
  integrationID: string;
  updatedParameterSchema: JSONObjectSchema | undefined;
  hasInputParameters: boolean | ScraperVersion | JSONObjectSchema | undefined;
  blueprintTestPayloads: BlueprintTestPayload[];
  setBlueprintTestPayloads: React.Dispatch<React.SetStateAction<BlueprintTestPayload[]>>;
  selectedTestPayload: BlueprintTestPayload | undefined | null;
  setSelectedTestPayload: React.Dispatch<
    React.SetStateAction<BlueprintTestPayload | null | undefined>
  >;
  canAutogenerateMappingTestForBlueprint: boolean;
  selectedTestLinkedAccount: undefined | string;
  setSelectedTestLinkedAccount: (s: string | undefined) => void;
  selectedTestCommonModel: undefined | string;
  setSelectedTestCommonModel: (s: string | undefined) => void;
  isTestingBlueprint: boolean;
  setIsTestingBlueprint: (x: boolean) => void;
  isLoading: boolean;
  setIsLoading: (x: boolean) => void;
  isLoadingTestLinkedAccounts: boolean;
  fetchTestLinkedAccounts: () => void;
  testLinkedAccounts: LinkedAccount[];
  testCommonModels: undefined | CommonModelInfo[];
  disableFilterByDate: boolean;
  setDisableFilterByDate: (x: boolean) => void;
  disableSyncCursor: boolean;
  setDisableSyncCursor: (x: boolean) => void;
  shouldFilterDisabledFields: boolean;
  setShouldFilterDisabledFields: (x: boolean) => void;
  shouldRunAsAsyncTask: boolean;
  setShouldRunAsAsyncTask: (x: boolean) => void;
  shouldGenerateMappingTest: boolean;
  setShouldGenerateMappingTest: (x: boolean) => void;
  asyncTaskExecutionID: string | undefined;
  setAsyncTaskExecutionID: (taskID: string | undefined) => void;
  asyncTaskExecutionResult?: AsyncTaskExecution;
  setAsyncTaskExecutionResult: (result?: AsyncTaskExecution) => void;
  shouldUseLastScraperResults: boolean;
  setShouldUseLastScraperResults: (x: boolean) => void;
  maxLoopIterations: number | undefined;
  setMaxLoopIterations: (x: number) => void;
  maxLoopIterationsPerStep: { [stepID: string]: number };
  setMaxLoopIterationsPerStep: (x: { [stepID: string]: number }) => void;
  maxPageIterations: number | undefined;
  setMaxPageIterations: (x: number) => void;
  frozenTimeValue: string | undefined;
  setFrozenTimeValue: (x: string) => void;
  overrideLastRunAtValue: string | undefined;
  setOverrideLastRunAtValue: (x: string) => void;
  shouldIncludeNestedParams: boolean;
  setShouldIncludeNestedParams: (x: boolean) => void;
  globalVarsAsString: string | undefined;
  initializedSchemaForDeletePayload: string;
  setGlobalVarsAsString: (x: string) => void;
  isShowingModelsModal: boolean;
  setIsShowingModelsModal: (x: boolean) => void;
  recentTestRuns: AsyncTaskExecutionPreview[];
  setRecentTestRuns: (x: AsyncTaskExecutionPreview[]) => void;
  isLoadingRecentTestRuns: boolean;
  setIsLoadingRecentTestRuns: (x: boolean) => void;
  isShowingRecentTestRunsModal: boolean;
  setIsShowingRecentTestRunsModal: (x: boolean) => void;
  doesBlueprintHaveUnsavedChanges: boolean;
  abstractConditionsForSelectiveSync: AbstractCondition[];
  dispatchAbstractConditionsForSelectiveSync: React.Dispatch<AbstractConditionAction>;
  isQBDIntegration: boolean;
  codeBlockBranch: CodeBlockBranchValue;
  setCodeBlockBranch: (x: CodeBlockBranchValue) => void;
}

const BlueprintEditorLeftPanelConsoleSubtab = ({
  integrationID,
  updatedParameterSchema,
  hasInputParameters,
  selectedTestLinkedAccount,
  setSelectedTestLinkedAccount,
  selectedTestCommonModel: selectedTestCommonModel,
  setSelectedTestCommonModel: setSelectedTestCommonModel,
  isTestingBlueprint,
  setIsTestingBlueprint,
  isLoading,
  setIsLoading,
  isLoadingTestLinkedAccounts,
  fetchTestLinkedAccounts,
  testLinkedAccounts,
  testCommonModels: testCommonModels,
  canAutogenerateMappingTestForBlueprint,
  disableFilterByDate,
  setDisableFilterByDate,
  disableSyncCursor,
  setDisableSyncCursor,
  shouldFilterDisabledFields,
  setShouldFilterDisabledFields,
  shouldRunAsAsyncTask,
  setShouldRunAsAsyncTask,
  shouldGenerateMappingTest,
  setShouldGenerateMappingTest,
  asyncTaskExecutionID,
  setAsyncTaskExecutionID,
  asyncTaskExecutionResult,
  setAsyncTaskExecutionResult,
  shouldUseLastScraperResults,
  setShouldUseLastScraperResults,
  maxLoopIterations,
  setMaxLoopIterations,
  maxLoopIterationsPerStep,
  setMaxLoopIterationsPerStep,
  maxPageIterations,
  setMaxPageIterations,
  frozenTimeValue,
  setFrozenTimeValue,
  overrideLastRunAtValue,
  setOverrideLastRunAtValue,
  shouldIncludeNestedParams,
  setShouldIncludeNestedParams,
  initializedSchemaForDeletePayload,
  globalVarsAsString,
  setGlobalVarsAsString,
  isShowingModelsModal,
  setIsShowingModelsModal,
  recentTestRuns,
  setRecentTestRuns,
  isLoadingRecentTestRuns,
  setIsLoadingRecentTestRuns,
  isShowingRecentTestRunsModal,
  setIsShowingRecentTestRunsModal,
  doesBlueprintHaveUnsavedChanges,
  blueprintTestPayloads,
  setBlueprintTestPayloads,
  abstractConditionsForSelectiveSync,
  dispatchAbstractConditionsForSelectiveSync,
  selectedTestPayload,
  setSelectedTestPayload,
  isQBDIntegration,
  codeBlockBranch,
  setCodeBlockBranch,
}: Props) => {
  const [isQBDLiveRunTab, setIsQBDLiveRunTab] = useState<boolean>(false);
  const [isShowingCommonModelDiffsDrawer, setIsShowingCommonModelDiffsDrawer] = useState(false);

  const [reportFileID, setReportFileID] = useState<string | undefined>();
  const [showReportUploadModal, setShowReportUploadModal] = useState<boolean>(false);
  const [shouldLogStepIO, setShouldLogStepIO] = useState<boolean>(true);
  const [
    isShowingAsyncTestRunUnsavedChangesModal,
    setIsShowingAsyncTestRunUnsavedChangesModal,
  ] = useState<boolean>(false);
  const {
    blueprint,
    blueprintRunnerExecutionResponse,
    setBlueprintRunnerExecutionResponse,
    setBlueprintParameterSchema,
    scrollToStep,
  } = useBlueprintContext();
  const [
    blueprintConfigurationError,
    setBlueprintConfigurationError,
  ] = useBlueprintConfigurationError(blueprint);

  // Validation for frozen time / last modified timestamp input
  const [isFrozenTimeError, setIsFrozenTimeError] = useState<boolean>(false);
  const [frozenTimeErrorText, setFrozenTimeErrorText] = useState<string | undefined>(undefined);
  const [isOverrideLastRunAtError, setIsOverrideLastRunAtError] = useState<boolean>(false);
  const [overrideLastRunAtErrorText, setOverrideLastRunAtErrorText] = useState<string | undefined>(
    undefined
  );

  const handleFrozenTimeInputChange = (value: string) =>
    handleTimeInputChange(value, setFrozenTimeValue, setIsFrozenTimeError, setFrozenTimeErrorText);

  const handleOverrideLastRunAtChange = (value: string) =>
    handleTimeInputChange(
      value,
      setOverrideLastRunAtValue,
      setIsOverrideLastRunAtError,
      setOverrideLastRunAtErrorText
    );

  const areInputParametersValid =
    !globalVarsAsString || (globalVarsAsString && isValidJSON(globalVarsAsString));

  const shouldDisableRunBlueprintForCodeBlockBranch = isRunBlueprintDisabledForCodeBlockBranch({
    codeBlockBranch,
  });

  const isTestBlueprintDisabled =
    isTestingBlueprint ||
    !selectedTestLinkedAccount ||
    !areInputParametersValid ||
    shouldDisableRunBlueprintForCodeBlockBranch;

  const keyMaps = {
    TEST: "ctrl+r",
  };

  const handlers = {
    TEST: (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isTestBlueprintDisabled) {
        runTestBlueprint({});
      }
    },
  };

  function runTestBlueprint({
    clearAsyncTaskExecutionResults,
  }: {
    clearAsyncTaskExecutionResults?: () => void;
  }) {
    if (
      abstractConditionsForSelectiveSync.length > 0 &&
      isAbstractConditionsForSelectiveSyncValid(abstractConditionsForSelectiveSync) === false
    ) {
      showErrorToast("Failed to run, check for incomplete selective sync filters");
      return;
    }
    if (shouldRunAsAsyncTask && doesBlueprintHaveUnsavedChanges) {
      setIsShowingAsyncTestRunUnsavedChangesModal(true);
      return;
    }
    setBlueprintRunnerExecutionResponse(null);
    setBlueprintConfigurationError(undefined);
    setAsyncTaskExecutionID(undefined);
    setAsyncTaskExecutionResult(undefined);
    clearAsyncTaskExecutionResults && clearAsyncTaskExecutionResults();
    setIsTestingBlueprint(true);
    setIsLoading(true);
    if (selectedTestLinkedAccount) {
      const shouldOverrideDisableByFilterDateToTrue = DISABLE_FILTER_BY_DATE_OPERATION_TYPES.includes(
        blueprint.operation_type
      );
      const shouldUseScraperResultsInsteadOfInputParams =
        blueprint.scraper && shouldUseLastScraperResults;
      // Convert Selective Sync values belonging to list operators, from string to array
      const convertedAbstractConditionsForSelectiveSync = prepareAbstractConditionsForTestRun(
        abstractConditionsForSelectiveSync
      );
      testBlueprint({
        blueprint,
        linkedAccountID: selectedTestLinkedAccount,
        disableFilterByDate: shouldOverrideDisableByFilterDateToTrue || disableFilterByDate,
        disableSyncCursor: shouldOverrideDisableByFilterDateToTrue || disableSyncCursor,
        maxLoopIterations: maxLoopIterations,
        maxLoopIterationsPerStep: maxLoopIterationsPerStep,
        maxPageIterations: maxPageIterations,
        frozenTimeValue: frozenTimeValue,
        overrideLastRunAtValue: overrideLastRunAtValue,
        reportFileID,
        globalVarTestValues:
          globalVarsAsString &&
          isValidJSON(globalVarsAsString) &&
          !shouldUseScraperResultsInsteadOfInputParams
            ? JSON.parse(globalVarsAsString)
            : null,
        commonModelObjectID: selectedTestCommonModel,
        shouldUseLastScraperResults,
        shouldFilterDisabledFields,
        shouldRunAsAsyncTask,
        shouldGenerateMappingTest,
        shouldLogStepIO,
        abstractConditionsForSelectiveSync: convertedAbstractConditionsForSelectiveSync,
        codeBlockBranch,
        onResponse: (response) => {
          if (response?.errors) {
            const errorMessage = (
              response?.errors?.map(
                (error: ValidationProblem) => `${error.title}: ${error.detail}`
              ) ?? []
            ).join("\n");
            showErrorToast(errorMessage);
            setIsTestingBlueprint(false);
          } else if (response?.scraper_results) {
            if (response.exit_code !== 200) {
              showErrorToast(`Scraper exited with code ${response.exit_code}`);
            }
            setBlueprintRunnerExecutionResponse(response.blueprint_results);
            setIsTestingBlueprint(false);
          } else if (response?.async_task_execution_id) {
            // Test run is running as async task.
            // Note: isTestingBlueprint is staying set to true, will be set to false only after
            // the async task execution concludes.
            setAsyncTaskExecutionID(response!.async_task_execution_id);
          } else {
            setBlueprintRunnerExecutionResponse(response);
            setIsTestingBlueprint(false);
            if (response?.exit_data?.debugging_info?.step_io_logging_failed === true) {
              showWarningToast(
                "Step I/O logging encountered an error, the Automation team has been contacted"
              );
            }
          }
          setIsLoading(false);
        },
        onError: (error) => {
          error
            ?.json()
            .then((value: string | BlueprintTestRunRequestErrorResponse) => {
              let errorMessage = "";
              if (typeof value == "string") {
                errorMessage = value;
              } else if (value.blueprint_error) {
                setBlueprintConfigurationError(value.blueprint_error);
                scrollToStep(value.blueprint_error.step_id);
                return;
              } else {
                errorMessage = getBlueprintTestRunErrorMessage(value);
              }
              showErrorToast(`A server error occurred. ${errorMessage}`);
            })
            .catch(() => {
              showErrorToast("A server error occurred");
            });
          setIsTestingBlueprint(false);
          setIsLoading(false);
        },
      });
    }
  }

  const fetchRecentTestRuns = () => {
    setRecentTestRuns([]);
    setIsLoadingRecentTestRuns(true);
    setIsShowingRecentTestRunsModal(true);
    fetchWithAuth({
      path: `/blueprints/versions/${blueprint.version.id}/test-runs?linked_account_id=${selectedTestLinkedAccount}`,
      method: "GET",
      onResponse: (data: PaginatedAPIResponse<AsyncTaskExecutionPreview>) => {
        setRecentTestRuns(data.results);
        setIsLoadingRecentTestRuns(false);
      },
      onError: () => {
        showErrorToast("Failed to fetch recent test runs.");
        setIsShowingRecentTestRunsModal(false);
        setIsLoadingRecentTestRuns(false);
      },
    });
  };

  const fetchAsyncTaskExecutionDetails = ({
    asyncExecutionID,
    onResponse,
    onError,
  }: {
    asyncExecutionID: string;
    onResponse: (data: AsyncTaskExecution) => void;
    onError: () => void;
  }) => {
    fetchWithAuth({
      path: `/blueprints/async-test-run/${asyncExecutionID}`,
      method: "GET",
      onResponse,
      onError,
    });
  };

  const isAsyncTaskExecutionCompletedPredicate = (execution: AsyncTaskExecution) =>
    !ASYNC_TASK_EXECUTION_RUNNING_STATUSES.includes(execution.status) &&
    !ASYNC_TASK_EXECUTION_QUEUED_STATUSES.includes(execution.status);

  // Periodically fetch results of async task execution, if an async bp test run is running.
  const [
    isTestingBlueprintAsync,
    asyncTaskExecutionResultFromUseFetchAsyncResult,
    clearAsyncTaskExecutionResults,
    pollAsyncTaskExecutionResults,
    stopPollingAsyncTaskExecutionResults,
  ] = useFetchAsyncResult<AsyncTaskExecution>(
    (props) => {
      if (!asyncTaskExecutionID) {
        return;
      }
      fetchAsyncTaskExecutionDetails({ asyncExecutionID: asyncTaskExecutionID, ...props });
    },
    fetchAsyncTaskExecutionDetails,
    "poll async task execution updates",
    isAsyncTaskExecutionCompletedPredicate
  );

  useEffect(() => {
    if (asyncTaskExecutionID) {
      pollAsyncTaskExecutionResults();
    }
  }, [asyncTaskExecutionID]);

  useEffect(() => {
    if (
      !asyncTaskExecutionResultFromUseFetchAsyncResult ||
      asyncTaskExecutionResultFromUseFetchAsyncResult.id !== asyncTaskExecutionID
    ) {
      return;
    }

    setAsyncTaskExecutionResult(asyncTaskExecutionResultFromUseFetchAsyncResult);

    if (asyncTaskExecutionResultFromUseFetchAsyncResult.blueprint_execution) {
      setBlueprintRunnerExecutionResponse(
        asyncTaskExecutionResultFromUseFetchAsyncResult.blueprint_execution
      );
    }

    if (isAsyncTaskExecutionCompletedPredicate(asyncTaskExecutionResultFromUseFetchAsyncResult)) {
      setIsTestingBlueprint(false);
    }
  }, [asyncTaskExecutionID, asyncTaskExecutionResultFromUseFetchAsyncResult]);

  const stopWaitingOnAsyncTaskExecution = () => {
    stopPollingAsyncTaskExecutionResults();
    setAsyncTaskExecutionID(undefined);
    setAsyncTaskExecutionResult(undefined);
    setIsTestingBlueprint(false);
  };

  const onLoadTestRun = (testRunId: string) => {
    if (testRunId === asyncTaskExecutionID) {
      // This test run is already loaded.
      return;
    }
    stopWaitingOnAsyncTaskExecution();
    setAsyncTaskExecutionID(testRunId);
    setAsyncTaskExecutionResult(undefined);
    setBlueprintRunnerExecutionResponse(null);
    setIsTestingBlueprint(true);
  };

  const { reportFiles } = useBlueprintContext();

  const updateToLatestInputParameterSchema = () => {
    if (updatedParameterSchema) {
      setBlueprintParameterSchema(updatedParameterSchema);
      const inputParams = initializeInputParams(
        updatedParameterSchema,
        shouldIncludeNestedParams,
        blueprint.operation_type
      );
      setGlobalVarsAsString(JSON.stringify(inputParams, null, 2));
    }
  };

  const shouldDisableGeneratePayload =
    !selectedTestLinkedAccount || doesBlueprintHaveUnsavedChanges;

  // QBD SECTION
  const {
    initiateLiveRun,
    isLoadingLiveRun,
    webConnectorSessionResponse,
  } = useInitiateWebConnectorLiveRun({
    blueprint,
    linkedAccountId: selectedTestLinkedAccount,
    globalVarsAsString: globalVarsAsString,
    overrideLastRunAt: overrideLastRunAtValue,
    shouldLogStepIO: shouldLogStepIO,
  });

  const displayForQBDFetchBlueprints =
    isQBDLiveRunTab && blueprint.operation_type === BlueprintOperationType.FETCH;

  const isFetchBlueprint =
    blueprint.operation_type === BlueprintOperationType.FETCH &&
    blueprint.trigger_type === BlueprintTriggerType.PERIODIC_TASK;

  // Gates copying produced model data — never offered for real customer Linked Accounts
  const isMergeOrgLinkedAccountSelected = testLinkedAccounts.some(
    (linkedAccount) =>
      linkedAccount.id === selectedTestLinkedAccount &&
      linkedAccount.organization?.is_merge_org === true
  );

  const isTestBlueprintDisabledOnlyForCodeBlockBranch =
    shouldDisableRunBlueprintForCodeBlockBranch &&
    !isTestingBlueprint &&
    !!selectedTestLinkedAccount &&
    !!areInputParametersValid;

  const shouldShowCodeBlockBranchDisabledTooltip =
    !isQBDIntegration &&
    !isQBDLiveRunTab &&
    isTestBlueprintDisabled &&
    isTestBlueprintDisabledOnlyForCodeBlockBranch;

  const runBlueprintDisabledTooltip = shouldShowCodeBlockBranchDisabledTooltip
    ? CODE_BLOCK_BRANCH_PRODUCTION_RUN_DISABLED_TOOLTIP
    : "";

  const runBlueprintButton = (
    <Button
      loading={isQBDLiveRunTab ? isLoadingLiveRun : isLoading}
      variant={ButtonVariant.PrimaryGray}
      disabled={isQBDLiveRunTab ? isLoadingLiveRun : isTestBlueprintDisabled}
      onClick={() => {
        isQBDLiveRunTab ? initiateLiveRun() : runTestBlueprint({ clearAsyncTaskExecutionResults });
      }}
      fullWidth={true}
    >
      Run Blueprint
    </Button>
  );
  const shouldShowCommonModelDiffsAction = canShowBlueprintCommonModelDiffsAction(
    blueprint.version.publish_state
  );

  return (
    <>
      <GlobalHotKeys allowChanges={true} keyMap={keyMaps} handlers={handlers} />
      <AsyncTestRunsModal
        show={isShowingRecentTestRunsModal}
        onHide={() => setIsShowingRecentTestRunsModal(false)}
        asyncTaskExecutionPreviews={recentTestRuns}
        isLoading={isLoadingRecentTestRuns}
        onLoadTestRun={onLoadTestRun}
      />
      <AsyncTestRunUnsavedChangesModal
        show={isShowingAsyncTestRunUnsavedChangesModal}
        onHide={() => setIsShowingAsyncTestRunUnsavedChangesModal(false)}
      />
      <BlueprintCommonModelDiffsDrawer
        open={isShowingCommonModelDiffsDrawer}
        onClose={() => setIsShowingCommonModelDiffsDrawer(false)}
        onViewExecutionLogs={(asyncTaskExecutionId) => {
          // Load the diff sub-run's step-I/O logs into the console (same path as loading a recent
          // test run), then close the drawer so the console results are visible.
          onLoadTestRun(asyncTaskExecutionId);
          setIsShowingCommonModelDiffsDrawer(false);
        }}
      />
      {showReportUploadModal && (
        <ReportFileUploadModal
          onHide={() => setShowReportUploadModal(false)}
          isVisible={showReportUploadModal}
          testLinkedAccounts={testLinkedAccounts}
          reportTemplateID={blueprint.report_template_id!}
          setReportFileID={setReportFileID}
          alreadySelectedLinkedAccountID={selectedTestLinkedAccount}
        />
      )}
      <div className="flex flex-col">
        <div className="flex flex-col space-y-4 pb-4">
          <LeftPanelSectionHeader title="Configure test run" />
          {isQBDIntegration && (
            <LiveRunChips
              isQBDLiveRunTab={isQBDLiveRunTab}
              setIsQBDLiveRunTab={setIsQBDLiveRunTab}
            />
          )}
          <BlueprintLeftPanelConsoleSelect
            integrationID={integrationID}
            selectedTestLinkedAccount={selectedTestLinkedAccount}
            setSelectedTestLinkedAccount={setSelectedTestLinkedAccount}
            isFetchBlueprint={isFetchBlueprint}
            fetchTestLinkedAccounts={fetchTestLinkedAccounts}
            setReportFileID={setReportFileID}
            isLoadingTestLinkedAccounts={isLoadingTestLinkedAccounts}
            testLinkedAccounts={testLinkedAccounts}
            selectedTestCommonModel={selectedTestCommonModel}
            setSelectedTestCommonModel={setSelectedTestCommonModel}
            testCommonModels={testCommonModels}
          />
          {!displayForQBDFetchBlueprints && (
            <BlueprintLeftPanelConsolePayload
              blueprint={blueprint}
              hasInputParameters={!!hasInputParameters}
              blueprintTestPayloads={blueprintTestPayloads}
              setBlueprintTestPayloads={setBlueprintTestPayloads}
              selectedTestPayload={selectedTestPayload}
              setSelectedTestPayload={setSelectedTestPayload}
              globalVarsAsString={globalVarsAsString}
              setSelectedTestCommonModel={setSelectedTestCommonModel}
              setGlobalVarsAsString={setGlobalVarsAsString}
              testLinkedAccounts={testLinkedAccounts}
              selectedTestLinkedAccount={selectedTestLinkedAccount}
              selectedTestCommonModel={selectedTestCommonModel}
              updatedParameterSchema={updatedParameterSchema}
              updateToLatestInputParameterSchema={updateToLatestInputParameterSchema}
              shouldDisableGeneratePayload={shouldDisableGeneratePayload}
              areInputParametersValid={!!areInputParametersValid}
              reportFileID={reportFileID}
              setReportFileID={setReportFileID}
              reportFiles={reportFiles}
              initializedSchemaForDeletePayload={initializedSchemaForDeletePayload}
            />
          )}
          <BlueprintLeftPanelConsoleSettings
            blueprint={blueprint}
            maxLoopIterations={maxLoopIterations}
            setMaxLoopIterations={setMaxLoopIterations}
            maxLoopIterationsPerStep={maxLoopIterationsPerStep}
            setMaxLoopIterationsPerStep={setMaxLoopIterationsPerStep}
            maxPageIterations={maxPageIterations}
            setMaxPageIterations={setMaxPageIterations}
            frozenTimeValue={frozenTimeValue}
            handleFrozenTimeInputChange={handleFrozenTimeInputChange}
            overrideLastRunAtValue={overrideLastRunAtValue}
            handleOverrideLastRunAtChange={handleOverrideLastRunAtChange}
            isFrozenTimeError={isFrozenTimeError}
            frozenTimeErrorText={frozenTimeErrorText}
            isOverrideLastRunAtError={isOverrideLastRunAtError}
            overrideLastRunAtErrorText={overrideLastRunAtErrorText}
            disableFilterByDate={disableFilterByDate}
            setDisableFilterByDate={setDisableFilterByDate}
            disableSyncCursor={disableSyncCursor}
            setDisableSyncCursor={setDisableSyncCursor}
            shouldFilterDisabledFields={shouldFilterDisabledFields}
            setShouldFilterDisabledFields={setShouldFilterDisabledFields}
            shouldUseLastScraperResults={shouldUseLastScraperResults}
            setShouldUseLastScraperResults={setShouldUseLastScraperResults}
            shouldRunAsAsyncTask={shouldRunAsAsyncTask}
            setShouldRunAsAsyncTask={setShouldRunAsAsyncTask}
            shouldIncludeNestedParams={shouldIncludeNestedParams}
            setShouldIncludeNestedParams={setShouldIncludeNestedParams}
            shouldGenerateMappingTest={shouldGenerateMappingTest}
            setShouldGenerateMappingTest={setShouldGenerateMappingTest}
            canAutogenerateMappingTestForBlueprint={canAutogenerateMappingTestForBlueprint}
            fetchRecentTestRuns={fetchRecentTestRuns}
            selectedTestLinkedAccount={selectedTestLinkedAccount}
            shouldLogStepIO={shouldLogStepIO}
            setShouldLogStepIO={setShouldLogStepIO}
            abstractConditionsForSelectiveSync={abstractConditionsForSelectiveSync}
            dispatchAbstractConditionsForSelectiveSync={dispatchAbstractConditionsForSelectiveSync}
            isQBDLiveRunTab={isQBDLiveRunTab}
            codeBlockBranch={codeBlockBranch}
            setCodeBlockBranch={setCodeBlockBranch}
          />
          {shouldRunAsAsyncTask && (
            <div className="text-center">
              <Button
                variant={ButtonVariant.SecondaryGray}
                onClick={fetchRecentTestRuns}
                disabled={!selectedTestLinkedAccount}
                fullWidth={true}
              >
                Show recent async runs
              </Button>
            </div>
          )}
          {blueprint.report_template_id && (
            <Button
              variant={ButtonVariant.SecondaryGray}
              fullWidth={true}
              onClick={() => setShowReportUploadModal(true)}
            >
              <b style={{ fontSize: "14px" }}>Upload new Report</b>
            </Button>
          )}
          {runBlueprintDisabledTooltip ? (
            <Tooltip title={runBlueprintDisabledTooltip}>
              <div className="w-full">{runBlueprintButton}</div>
            </Tooltip>
          ) : (
            runBlueprintButton
          )}
          {shouldShowCommonModelDiffsAction && (
            <Button
              variant={ButtonVariant.SecondaryGray}
              fullWidth={true}
              leftIcon={<GitCompare size={14} />}
              onClick={() => setIsShowingCommonModelDiffsDrawer(true)}
            >
              Common Model diffs
            </Button>
          )}
        </div>
        {isQBDLiveRunTab ? (
          <WebConnectorLiveRunResults
            isLoadingLiveRun={isLoadingLiveRun}
            webConnectorSessionResponse={webConnectorSessionResponse}
            isTestingBlueprintAsync={isTestingBlueprintAsync}
            stopWaitingOnAsyncTaskExecution={stopWaitingOnAsyncTaskExecution}
            asyncTaskExecutionResult={asyncTaskExecutionResult}
            blueprintRunnerExecutionResponse={blueprintRunnerExecutionResponse}
            isTestingBlueprint={isTestingBlueprint}
            isShowingModelsModal={isShowingModelsModal}
            setIsShowingModelsModal={setIsShowingModelsModal}
          />
        ) : blueprintConfigurationError ? (
          <BlueprintLeftPanelConsoleValidationError
            error={blueprintConfigurationError}
            onViewStep={() => scrollToStep(blueprintConfigurationError.step_id)}
          />
        ) : (
          <>
            <div>
              <BlueprintLeftPanelConsoleResultSummary
                isTestingBlueprintAsync={isTestingBlueprintAsync}
                stopWaitingOnAsyncTaskExecution={stopWaitingOnAsyncTaskExecution}
                asyncTaskExecutionResult={asyncTaskExecutionResult}
                blueprintRunnerExecutionResponse={blueprintRunnerExecutionResponse}
                isTestingBlueprint={isTestingBlueprint}
                isShowingModelsModal={isShowingModelsModal}
                setIsShowingModelsModal={setIsShowingModelsModal}
                isMergeOrgLinkedAccountSelected={isMergeOrgLinkedAccountSelected}
              />
            </div>
            <div>
              <BlueprintLeftPanelConsoleResultLogs
                blueprintRunnerExecutionResponse={blueprintRunnerExecutionResponse}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default BlueprintEditorLeftPanelConsoleSubtab;
